import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import Button from "components/buttons/Button";
import CopyToClipboard from "components/CopyToClipboard";
import {
  CAT_GATED_HREF,
  catGatedDisabledLinkClassName,
  catGatedOpenLinkClassName,
  handleCatGatedAnchorOpen,
} from "pages/project/common/taskBase/list/catGatedAnchor";
import { MouseEvent } from "react";
import { LoaderIcon } from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface Props {
  isLoading: boolean;
  label: string;
  canCopy: boolean;
  url: string | null;
  onClick?: () => void;
  /** Gates opening the link only; generating the link and copying it stay untouched. */
  onBeforeOpen?: (open: () => void) => void;
  /** Non-interactive open control (e.g. while mandatory-instruction ack is still loading/failed). */
  disabled?: boolean;
}

export const ShareLink = ({ isLoading, url, canCopy, label, onClick, onBeforeOpen, disabled }: Props) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex flex-nowrap items-center gap-2">
        <LoaderIcon />
        <span title={label} className="max-w-[200px] truncate sm:max-w-lg">
          {label}
        </span>
      </div>
    );
  }

  if (url === null) {
    return (
      <div className="flex flex-nowrap items-center gap-2">
        <Button color="ternary" className="hidden px-2 py-1 text-xs font-medium sm:block" onClick={() => onClick?.()}>
          {t("task.detail.jobLinks.generate-link")}
        </Button>
        <Button color="ternary" className="px-1.5 py-0.5 text-xs font-medium sm:hidden" onClick={() => onClick?.()}>
          <ArrowPathIcon className="size-4" />
        </Button>
        <span title={label} className="max-w-[200px] truncate sm:max-w-lg">
          {label}
        </span>
      </div>
    );
  }

  const openUrl = () => window.open(url, "_blank", "noopener,noreferrer");

  const handleGatedOpen = (event: MouseEvent<HTMLAnchorElement>) => {
    handleCatGatedAnchorOpen(event, () => {
      onClick?.();
      onBeforeOpen?.(openUrl);
    });
  };

  const linkLabel = (
    <span className="flex flex-nowrap items-center gap-1">
      <ArrowTopRightOnSquareIcon className="size-4" />
      <span title={label} className="max-w-[200px] truncate sm:max-w-lg">
        {label}
      </span>
    </span>
  );

  // Disabled: same fail-closed UX as Download — visible but not interactive (no silent runOrConfirm no-op).
  const link = disabled ? (
    <span aria-disabled="true" data-test="link-open-xtm-link" className={catGatedDisabledLinkClassName}>
      {linkLabel}
    </span>
  ) : onBeforeOpen ? (
    // When gated, do not expose a real href — middle-click / open-in-new-tab would bypass the modal.
    <a
      href={CAT_GATED_HREF}
      role="button"
      data-test="link-open-xtm-link"
      data-dd-action-name="btn-open-xtm-link"
      className={catGatedOpenLinkClassName}
      onClick={handleGatedOpen}
      onAuxClick={handleGatedOpen}
    >
      {linkLabel}
    </a>
  ) : (
    <a
      rel="noopener noreferrer"
      target="_blank"
      data-test="link-open-xtm-link"
      data-dd-action-name="btn-open-xtm-link"
      className={catGatedOpenLinkClassName}
      onClick={() => onClick?.()}
      href={url}
    >
      {linkLabel}
    </a>
  );

  if (canCopy)
    return (
      <CopyToClipboard value={url} onClick={onClick}>
        {link}
      </CopyToClipboard>
    );
  return link;
};
